import React, { useState, useCallback, createContext, useContext } from 'react';
import api from '../services/api';

import newProductRequest from '../services/newProduct';
import newProdutoDisponibilidade from '../services/newProdutoDisponibilidade';

import { useAuth } from './auth';

export type Tamanho = {
  id: number;
  descricao: string;
  isSelected?: boolean;
  preco: number;
  codigo?: number;
  estoque_minimo: number;
  estoque_atual: number;
  estoque_id?: number;
  produto_grade_id?: number;
  preco_id?: number;
  qtd_pedacos: number;
  qtd_sabores: number;
};

export type ItemComplemento = {
  id: string | number;
  descricao: string;
  imagem?: File;
  preco: number;
  empresa_id: number;
  codigo_complemento?: string;
  qtd_min: number;
  tipo: number;
  qtd_max: number;
  produto_id?: number;
  index_complemento: number;
  pausar_venda?: boolean;
  isNew: boolean;
  ordem: number;
};

export type Complemento = {
  id?: number;
  index: number;
  descricao: string;
  complemento_obrigatorio: boolean;
  tipo: number;
  qtd_min: number;
  qtd_max: number;
  empresa_id: number;
  isNew: boolean;
  ordem: number;
  ativo?: boolean;
  pausar_venda?: boolean;
};

type TurnoType = {
  inicio: number;
  fim: number;
};

type DisponibilidadeData = {
  dias: {
    domingo: boolean;
    segunda: boolean;
    terca: boolean;
    quarta: boolean;
    quinta: boolean;
    sexta: boolean;
    sabado: boolean;
  };
  turnos?: TurnoType[];
};

export type ProdutoPizzaData = {
  id?: number;
  grupo_id: number;
  descricao: string;
  observacao?: string;
  unidade_medida_id?: number;
  grupo_produto_id?: number;
  codigo?: string;
  imagem?: File | string;
  grupo_tamanhos: Tamanho[];
  tamanhos: Tamanho[];
  gtin?: string;
  complementos: Complemento[];
  itens_complemento: ItemComplemento[];
  disponibilidade: DisponibilidadeData;
};

interface ProdutoPizzaContextData {
  produtoId?: number;
  drawerIsOpen: boolean;
  currentTabIndex: number;
  tamanhosIsEnabled: boolean;
  complementosIsEnabled: boolean;
  selectedComplementoIndex: number;
  disponibilidadeIsEnabled: boolean;
  complementosDrawerIsOpen: boolean;
  produtoPizzaData: ProdutoPizzaData;
  itensComplementosIsEnabled: boolean;
  currentComplementosTabIndex: number;
  cloning: boolean;

  openDrawer(): void;
  resetDrawer(): void;
  enableTamanhos(): void;
  enableComplementos(): void;
  resetComplementosTab(): void;
  enableDisponibilidade(): void;
  enableItensComplementos(): void;
  toggleComplementosDrawer(): void;
  toggleEditingProduto(id: number): void;
  deleteComplemento(index: number): void;
  createNewProdutoPizza(
    disponibilidadeData: DisponibilidadeData,
  ): Promise<void>;
  updateProdutoPizza(disponibilidadeData: DisponibilidadeData): Promise<void>;
  changeCurrentTabIndex(tabIndex: number): void;
  changeProdutoPizzaData(produtoPizzaData: any): void;
  changeSelectedComplementoIndex(index: number): void;
  changeCurrentComplementosTabIndex(tabIndex: number): void;
  setCloning(value: boolean): void;
}

const ProdutoPizzaContext = createContext<ProdutoPizzaContextData>(
  {} as ProdutoPizzaContextData,
);

const ProdutoPizzaProvider: React.FC = ({ children }) => {
  const { user } = useAuth();

  const [produtoPizzaData, setProdutoPizzaData] = useState<ProdutoPizzaData>({
    tamanhos: [],
    itens_complemento: [],
    complementos: [],
    grupo_tamanhos: [],
  } as any);
  const [drawerIsOpen, setDrawerIsOpen] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  const [tamanhosIsEnabled, setTamanhosIsEnabled] = useState(false);
  const [disponibilidadeIsEnabled, setDisponibilidadeIsEnabled] = useState(
    false,
  );
  const [complementosIsEnabled, setComplementosIsEnabled] = useState(false);
  const [complementosDrawerIsOpen, setComplementosDrawerIsOpen] = useState(
    false,
  );
  const [
    currentComplementosTabIndex,
    setCurrentComplementosTabIndex,
  ] = useState(0);
  const [itensComplementosIsEnabled, setItensComplementosIsEnabled] = useState(
    false,
  );
  const [selectedComplementoIndex, setSelectedComplementoIndex] = useState(0);
  const [produtoId, setProdutoId] = useState<number>();

  const openDrawer = useCallback(() => {
    setDrawerIsOpen(true);
  }, []);

  const toggleEditingProduto = useCallback((id: number) => {
    setProdutoId(id);
  }, []);

  const resetComplementosTab = useCallback(() => {
    setComplementosDrawerIsOpen(false);
    setCurrentComplementosTabIndex(0);
    setItensComplementosIsEnabled(false);
  }, []);

  const resetDrawer = useCallback(() => {
    setDrawerIsOpen(false);
    setCurrentTabIndex(0);
    setTamanhosIsEnabled(false);
    setDisponibilidadeIsEnabled(false);
    setComplementosIsEnabled(false);
    setProdutoPizzaData({
      tamanhos: [],
      itens_complemento: [],
      complementos: [],
      grupo_tamanhos: [],
    } as any);
    resetComplementosTab();
    setProdutoId(0);
    setCloning(false);
  }, [resetComplementosTab]);

  const changeProdutoPizzaData = useCallback((produtoPizzaPartialData: any) => {
    setProdutoPizzaData((oldState) => ({
      ...oldState,
      ...produtoPizzaPartialData,
    }));
  }, []);

  const enableItensComplementos = useCallback(() => {
    setItensComplementosIsEnabled(true);
  }, []);

  const changeCurrentComplementosTabIndex = useCallback((tabIndex: number) => {
    setCurrentComplementosTabIndex(tabIndex);
  }, []);

  const enableTamanhos = useCallback(() => {
    setTamanhosIsEnabled(true);
  }, []);

  const enableComplementos = useCallback(() => {
    setComplementosIsEnabled(true);
  }, []);

  const changeSelectedComplementoIndex = useCallback((index: number) => {
    setSelectedComplementoIndex(index);
  }, []);

  const enableDisponibilidade = useCallback(() => {
    setDisponibilidadeIsEnabled(true);
  }, []);

  const changeCurrentTabIndex = useCallback((tabIndex: number) => {
    setCurrentTabIndex(tabIndex);
  }, []);

  const toggleComplementosDrawer = useCallback(() => {
    setComplementosDrawerIsOpen((oldState) => !oldState);
  }, []);

  const deleteComplemento = useCallback(
    async (index: number) => {
      const findComplemento = produtoPizzaData.complementos.find(
        (complemento) => complemento.index === index,
      );

      if (findComplemento) {
        const complementos = produtoPizzaData.complementos.filter(
          (complemento) => complemento.index !== index,
        );
        const itens_complemento = produtoPizzaData.itens_complemento.filter(
          (item) => item.index_complemento !== index,
        );

        setProdutoPizzaData({
          ...produtoPizzaData,
          complementos,
          itens_complemento,
        });

        if (typeof findComplemento.id === 'number') {
          await api.post(`/complemento/${findComplemento.id}`);
        }
      }
    },
    [produtoPizzaData],
  );

  const getBlobFromUrl = (myImageUrl: string): Promise<any> => {
    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest();
      request.open('GET', myImageUrl, true);
      request.responseType = 'blob';
      request.onload = () => {
        resolve(request.response);
      };
      request.onerror = reject;
      request.send();
    });
  };

  const getDataFromBlob = (
    myBlob: Blob,
  ): Promise<string | ArrayBuffer | null> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve(reader.result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(myBlob);
    });
  };

  const createNewProdutoPizza = useCallback(
    async ({ dias, turnos = [] }: DisponibilidadeData) => {
      const {
        descricao,
        grupo_id,
        unidade_medida_id,
        codigo,
        observacao,
        imagem,
        gtin,
      } = produtoPizzaData;

      let newImage: File | undefined;

      if (typeof imagem === 'string') {
        const response = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/files/${imagem}`,
        );

        const blobFile = await response.blob();

        newImage = new File([blobFile], imagem.split('-')[1]);
      }

      const { grupo_produto_id, produto_id } = await newProductRequest({
        descricao,
        empresa_id: user.empresa_id,
        grupo_id,
        preco: 0,
        tipo: 3,
        unidade_medida_id: unidade_medida_id || 0,
        codigo,
        controla_grade: 2,
        observacao,
        imagem: newImage || imagem,
        gtin,
      });

      produtoPizzaData.tamanhos.forEach(async (tamanho) => {
        const produtoGradeResponse = await api.post('/produto/grade', {
          empresa_id: user.empresa_id,
          tamanho_id: tamanho.id,
          produto_id,
          grupo_id,
          grupo_produto_id,
          descricao: tamanho.descricao,
          ordem: 1,
          preco: Number(tamanho.preco) || 0,
          estoque_controlado: true,
          ativo: tamanho.isSelected,
          codigo_grade: tamanho.codigo,
          qtd_pedacos: tamanho.qtd_pedacos,
          qtd_sabores: tamanho.qtd_sabores,
          tipo: 3,
        });

        await api.post('/estoque', {
          produto_id,
          produto_grade_id: produtoGradeResponse.data.id,
          empresa_id: user.empresa_id,
          estoque_minimo: tamanho.estoque_minimo || 0,
          estoque_atual: tamanho.estoque_atual || 0,
          ativo: tamanho.isSelected,
        });
      });

      await newProdutoDisponibilidade({
        produto_id,
        role: { dias, turnos },
      });

      if (produtoPizzaData.complementos.length > 0) {
        const complementoData = new FormData();

        const itensComplementoArray = produtoPizzaData.itens_complemento.map(
          (item) => {
            if (item.imagem) {
              complementoData.append('item_complemento_img', item.imagem);
            }

            return {
              codigo_complemento: item.codigo_complemento,
              descricao: item.descricao,
              tipo: 3,
              nome_imagem: item.imagem ? item.imagem.name : undefined,
              produto_id: item.produto_id,
              index_complemento: item.index_complemento,
              preco: item.preco,
              qtd_min: item.qtd_min,
              qtd_max: item.qtd_max,
              empresa_id: item.empresa_id,
            };
          },
        );

        complementoData.append(
          'itens_complemento',
          JSON.stringify(itensComplementoArray),
        );
        complementoData.append(
          'complementos',
          JSON.stringify(produtoPizzaData.complementos),
        );
        complementoData.append('grupo_produto_id', String(grupo_produto_id));

        await api.post('/complemento', complementoData, {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'multipart/form-data',
          },
        });
      }
    },
    [produtoPizzaData, user],
  );

  const updateProdutoPizza = useCallback(
    async ({ dias, turnos }: DisponibilidadeData) => {
      const {
        descricao,
        grupo_id,
        unidade_medida_id,
        codigo,
        observacao,
        gtin,
        grupo_produto_id,
        imagem,
      } = produtoPizzaData;

      await api.put(`/produto/disponibilidade/${produtoId}`, {
        role: { dias, turnos },
        ativo: true,
      });

      if (imagem && typeof imagem !== 'string') {
        const produtoImagedata = new FormData();

        produtoImagedata.append('produto_img', imagem);

        await api.patch(`/produto/${produtoId}`, produtoImagedata);
      }

      await api.put(`/produto/${produtoId}`, {
        descricao,
        empresa_id: user.empresa_id,
        grupo_id,
        preco: 0,
        tipo: 3,
        unidade_medida_id: unidade_medida_id || 0,
        codigo,
        observacao,
        gtin,
      });

      await Promise.all(
        produtoPizzaData.tamanhos
          .filter((tamanho) => tamanho.isSelected && !tamanho.produto_grade_id)
          .map(async (tamanho) => {
            const produtoGradeResponse = await api.post('/produto/grade', {
              empresa_id: user.empresa_id,
              tamanho_id: tamanho.id,
              produto_id: produtoId,
              grupo_id,
              grupo_produto_id,
              descricao: tamanho.descricao,
              ordem: 1,
              preco: Number(tamanho.preco) || 0,
              estoque_controlado: true,
              ativo: tamanho.isSelected,
              codigo_grade: tamanho.codigo,
              qtd_pedacos: tamanho.qtd_pedacos,
              qtd_sabores: tamanho.qtd_sabores,
              tipo: 3,
            });

            await api.post('/estoque', {
              produto_id: produtoId,
              produto_grade_id: produtoGradeResponse.data.id,
              empresa_id: user.empresa_id,
              estoque_minimo: tamanho.estoque_minimo || 0,
              estoque_atual: tamanho.estoque_atual || 0,
              ativo: tamanho.isSelected,
            });
          }),
      );

      await Promise.all(
        produtoPizzaData.tamanhos
          .filter((tamanho) => tamanho.produto_grade_id)
          .map(async (tamanho) => {
            await api.put(`/produto/grade/${tamanho.produto_grade_id}`, {
              empresa_id: user.empresa_id,
              tamanho_id: tamanho.id,
              produto_id: produtoId,
              grupo_produto_id: tamanho.produto_grade_id,
              codigo_grade: tamanho.codigo,
              descricao: tamanho.descricao,
              estoque_controlado: true,
              pausar_venda: false,
              ativo: tamanho.isSelected,
            });

            await api.put(`/estoque/${produtoId}`, {
              estoque_minimo: tamanho.estoque_minimo || 0,
              estoque_atual: tamanho.estoque_atual || 0,
              produto_grade_id: tamanho.produto_grade_id,
              ativo: tamanho.isSelected,
            });

            await api.put(`/produto/preco/${produtoId}`, {
              tabela_preco_id: 1,
              valor: Number(tamanho.preco) || 0,
              grupo_id: produtoPizzaData.grupo_id,
              produto_grade_id: tamanho.produto_grade_id,
            });
          }),
      );

      produtoPizzaData.itens_complemento.forEach(async (item) => {
        if (item.isNew && typeof item.id === 'number') {
          if (item.imagem && typeof item.imagem !== 'string') {
            const itemComplementoData = new FormData();

            itemComplementoData.append('item_complemento_img', item.imagem);

            await api.patch(
              `/complemento/item/${item.id}`,
              itemComplementoData,
            );
          }
        }
      });

      const editedItensComplementos = produtoPizzaData.itens_complemento
        .filter((item) => item.isNew && typeof item.id === 'number')
        .map((item) => ({
          ...item,
          isNew: false,
        }));

      if (editedItensComplementos.length > 0) {
        await api.put('/complemento/item', {
          itens_complemento: JSON.stringify(editedItensComplementos),
        });
      }

      const newItensComplementos = produtoPizzaData.itens_complemento
        .filter(
          (item) =>
            item.isNew &&
            typeof item.id === 'string' &&
            produtoPizzaData.complementos.find(
              (complemento) => complemento.index === item.index_complemento,
            )?.id,
        )
        .map((item) => ({
          ...item,
          complemento_id: produtoPizzaData.complementos.find(
            (complemento) => complemento.index === item.index_complemento,
          )?.id,
          hasImage: !!item.imagem,
          isNew: true,
        }));

      if (newItensComplementos.length > 0) {
        const newItensComplementosData = new FormData();

        newItensComplementos
          .filter((item) => item.hasImage)
          .forEach((item) => {
            item.imagem &&
              newItensComplementosData.append(
                'item_complemento_img',
                item.imagem,
              );
          });

        newItensComplementosData.append(
          'itens_complemento',
          JSON.stringify(newItensComplementos),
        );

        await api.put('/complemento/item', newItensComplementosData, {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'multipart/form-data',
          },
        });
      }

      produtoPizzaData.complementos.forEach(async (complemento) => {
        if (complemento.isNew && typeof complemento.id === 'number') {
          await api.put(`/complemento/${complemento.id}`, {
            ordem: complemento.ordem,
            descricao: complemento.descricao,
            qtd_min: complemento.qtd_min,
            qtd_max: complemento.qtd_max,
            complemento_obrigatorio: complemento.complemento_obrigatorio,
            pausar_venda: complemento.pausar_venda,
            ativo: complemento.ativo,
            tipo: 3,
          });
        }
      });

      const newComplementos = produtoPizzaData.complementos.filter(
        (complemento) =>
          complemento.isNew && typeof complemento.id !== 'number',
      );

      if (newComplementos.length > 0) {
        const complementoData = new FormData();

        const itensComplementoArray = produtoPizzaData.itens_complemento
          .filter((item) => item.isNew && typeof item.id === 'string')
          .map((item) => {
            if (item.imagem) {
              complementoData.append('item_complemento_img', item.imagem);
            }

            return {
              codigo_complemento: item.codigo_complemento,
              descricao: item.descricao,
              tipo: item.tipo,
              nome_imagem: item.imagem ? item.imagem.name : undefined,
              produto_id: item.produto_id,
              index_complemento: item.index_complemento,
              preco: item.preco,
              qtd_min: item.qtd_min,
              qtd_max: item.qtd_max,
              empresa_id: item.empresa_id,
              pausar_venda: item.pausar_venda,
            };
          });

        complementoData.append(
          'itens_complemento',
          JSON.stringify(itensComplementoArray),
        );
        complementoData.append('complementos', JSON.stringify(newComplementos));
        complementoData.append('grupo_produto_id', String(grupo_produto_id));

        await api.post('/complemento', complementoData, {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'multipart/form-data',
          },
        });
      }
    },
    [produtoPizzaData, user, produtoId],
  );

  return (
    <ProdutoPizzaContext.Provider
      value={{
        produtoId,
        drawerIsOpen,
        currentTabIndex,
        produtoPizzaData,
        tamanhosIsEnabled,
        complementosIsEnabled,
        disponibilidadeIsEnabled,
        complementosDrawerIsOpen,
        selectedComplementoIndex,
        itensComplementosIsEnabled,
        currentComplementosTabIndex,
        cloning,
        setCloning,
        openDrawer,
        resetDrawer,
        enableTamanhos,
        deleteComplemento,
        updateProdutoPizza,
        enableComplementos,
        toggleEditingProduto,
        resetComplementosTab,
        enableDisponibilidade,
        createNewProdutoPizza,
        changeCurrentTabIndex,
        changeProdutoPizzaData,
        enableItensComplementos,
        toggleComplementosDrawer,
        changeSelectedComplementoIndex,
        changeCurrentComplementosTabIndex,
      }}
    >
      {children}
    </ProdutoPizzaContext.Provider>
  );
};

function useProdutoPizza(): ProdutoPizzaContextData {
  const context = useContext(ProdutoPizzaContext);

  if (!context)
    throw new Error(
      'useProdutoPizza deve ser utilizado junto do ProdutoPizzaProvider',
    );

  return context;
}

export { ProdutoPizzaProvider, useProdutoPizza };
